import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import * as Styled from './styles';

/**
 * Compact pager for a table/gallery toolbar: "Page 3 of 444" between first / previous and next /
 * last arrows, borderless and drawn in forest at the size of the counts beside it (see ./styles).
 *
 * A shortcut, not a replacement: the footer TableSizeControls keeps the rows-per-page and
 * jump-to-page selects and the "21 - 40 of 8,865" record range. `onPageChange` takes the same
 * `({ value })` argument as the footer's handlers, so a consumer passes the very same function to
 * both.
 *
 * Deliberately page numbers only — no record range, no total. The totals belong to whatever count
 * readout the toolbar already has (they must: this whole control unmounts when there is only one
 * page), and a range beside a page-of-pages says the same thing twice while doubling the width of
 * the control.
 *
 * Unlike the footer's PaginationNavigation, the arrows are DISABLED at the bounds rather than
 * unmounted — a control sitting between other toolbar items must not change width as you page.
 */
const InlinePager = ({ page, pages, onPageChange, titleTable }) => {
  // Nothing to page through — stay out of the toolbar entirely.
  if (pages <= 1) return null;

  // "Retailers " -> "next Retailers page"; empty -> "next page".
  const noun = titleTable ? `${titleTable} ` : '';

  return (
    <Styled.Wrapper aria-label={`${noun}pagination`}>
      <Styled.Button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange({ value: 1 })}
        aria-label={`first ${noun}page`}
      >
        <FiChevronsLeft />
      </Styled.Button>
      <Styled.Button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange({ value: page - 1 })}
        aria-label={`previous ${noun}page`}
      >
        <FiChevronLeft />
      </Styled.Button>
      <Styled.Label>
        Page <strong>{page.toLocaleString()}</strong> of {pages.toLocaleString()}
      </Styled.Label>
      <Styled.Button
        type="button"
        disabled={page >= pages}
        onClick={() => onPageChange({ value: page + 1 })}
        aria-label={`next ${noun}page`}
      >
        <FiChevronRight />
      </Styled.Button>
      <Styled.Button
        type="button"
        disabled={page >= pages}
        onClick={() => onPageChange({ value: pages })}
        aria-label={`last ${noun}page`}
      >
        <FiChevronsRight />
      </Styled.Button>
    </Styled.Wrapper>
  );
};

InlinePager.propTypes = {
  page: PropTypes.number.isRequired,
  pages: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  // Noun for the aria-labels ("Retailers" -> "next Retailers page"), matching the footer's
  // PaginationNavigation prop of the same name.
  titleTable: PropTypes.string,
};

InlinePager.defaultProps = {
  pages: 0,
  titleTable: '',
};

export default memo(InlinePager);
